<template>
  <div>
    <slot name="addButton">
      <v-btn
        color="primary"
        class="mt-2"
        @click="showDialog = true"
      >
        New Series
      </v-btn>
    </slot>
    <v-dialog
      v-model="showDialog"
      persistent
      max-width="500px"
    >
      <v-card>
        <v-card-title>New Series</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col>
              <v-form
                ref="form"
                v-model="isValid"
                aria-label="New Series"
                class="form-share"
              >
                <v-select
                  v-model="selectedName"
                  :items="validSeriesSelections"
                  :rules="rules.selectOne(validSeriesSelections)"
                  label="What is the name for this series?"
                  autofocus
                />
                <v-text-field
                  v-if="otherNameSelect"
                  v-model="name"
                  label="What is the other name for this series?"
                  :rules="rules.text"
                  :counter="MAX_LENGTH"
                  clearable
                />
                <v-text-field
                  v-model="valuation"
                  :rules="rules.number"
                  prefix="$"
                  label="What is the company valued at for this series?"
                  type="number"
                  min="0"
                  required
                />
                <v-text-field
                  v-model="amountToRaise"
                  :rules="[...rules.number, ...rules.lessThan([valuation, 'Company Value'])]"
                  prefix="$"
                  label="How much money is to be raised for this series?"
                  type="number"
                  min="1"
                  required
                />
                <v-text-field
                  v-model="numberOfShares"
                  :rules="rules.number"
                  label="How many shares is to be issued for this series?"
                  type="number"
                  min="1"
                  required
                />
              </v-form>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-chip
                outlined
                role="status"
                aria-label="Amount To Sell"
              >
                Amount to Sell {{ formatedTotalDilution() }}
              </v-chip>
            </v-col>
            <v-col>
              <v-chip
                outlined
                role="status"
                aria-label="Price per share"
              >
                Price per share {{ formatedPricePerShare() }}
              </v-chip>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col>
              <v-alert
                v-if="showCloseSeriesWarning"
                type="warning"
                dense
                dismissible
              >
                Opening a new series will close the current open series
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            large
            @click="cancelForm"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="!isValid"
            :loading="loading"
            color="primary"
            large
            @click.stop="onSubmitData"
          >
            Add Series
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'FormSeries',
  props: {
    showCloseSeriesWarning: { type: Boolean, default: false },
    seriesList: { type: Array, required: true },
    otherSeriesName: { type: String, default: 'Other' },
    defaultSeriesList: { type: Array, default: () => ['Founders', 'Seed', 'A'] }
  },
  data: function () {
    return {
      loading: false,
      showDialog: false,
      isValid: false,
      numberOfShares: undefined,
      valuation: undefined,
      amountToRaise: undefined,
      name: undefined,
      selectedName: undefined,
      MAX_LENGTH: 120,
      rules: {
        text: [value => this.rulesAction(value, 'text')],
        number: [value => this.rulesAction(value, 'number')],
        email: [value => this.rulesAction(value, 'email')],
        lessThan: (lessThanValue) => [value => this.rulesAction(value, 'lessThan', lessThanValue)],
        selectOne: (list) => [value => this.rulesAction(value, 'selectOne', list)]
      }
    }
  },
  computed: {
    validSeriesSelections() {
      return this.defaultSeriesList.filter((item) => !this.seriesList.includes(item)).concat(this.otherSeriesName)
    },
    otherNameSelect() {
      return this.selectedName === this.otherSeriesName
    }
  },
  methods: {
    formatedTotalDilution() {
      const valuation = parseInt(this.valuation || 0)
      const amountToRaise = parseInt(this.amountToRaise || 0)
      let percentage = ((amountToRaise / valuation) * 100)
      if (!Number.isFinite(percentage)) {
        percentage = 0
      }

      const formatedPercentage = percentage.toFixed(2).concat('%')
      return `${amountToRaise}/${valuation} (${formatedPercentage})`
    },
    cancelForm() {
      this.showDialog = false
      this.$refs.form.reset()
    },
    formatedPricePerShare() {
      const numberOfShares = parseInt(this.numberOfShares || 0)
      const amountToRaise = parseInt(this.amountToRaise || 0)
      let ratio = amountToRaise / numberOfShares
      if (!Number.isFinite(ratio)) {
        ratio = 0
      }

      return `$ ${ratio.toFixed(2)}`
    },
    onSubmitData() {
      if (this.isValid) {
        this.loading = true
        const name = this.selectedName === this.otherSeriesName ? this.name : this.selectedName
        this.$handlingError('Failed to add new share series',
          () => this.$api.addShareSeries({
            numberOfShares: this.numberOfShares,
            valuation: this.valuation,
            amountToRaise: this.amountToRaise,
            name
          })
            .then(() => {
              this.showDialog = false
              this.$showSuccessbar('Share series added!')
              this.$emit('onSubmitData')
              this.$refs.form.reset()
            })
            .finally(() => {
              this.loading = false
            })
        ).catch(() => {})
      }
    },
    rulesAction(value, type, extra) {
      if (!value) {
        return 'This field is required.'
      }

      switch (type) {
      case 'text':
        return value.length <= this.MAX_LENGTH || `Max ${this.MAX_LENGTH} characters`
      case 'lessThan':
        return (parseInt(value) < parseInt(extra[0])) || `Must be less than ${extra[1]}`
      case 'number':
        return (Number.isInteger(parseFloat(value)) && parseInt(value) > 0) || 'Just whole numbers greater than 0 are accepted'
      case 'selectOne':
        return extra.includes(value) || 'Select a valid option from the list'
      }
    }
  }
}
</script>
